import React, { Fragment, useContext, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import 'react-slidedown/lib/slidedown.css'
import { XCircle } from '@styled-icons/boxicons-solid'
import HarnessesSearchTable from './HarnessesSearchTable'
import HarnessLevelsSearchTable from './HarnessLevelsSearchTable'
import {
  handleEnterKey,
  SELECT_ALL,
  DESELECT_ALL,
  downloadAsZip,
} from './utils'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import LoadingSpinnerOverlay from '../common/LoadingSpinnerOverlay'
import { NavContext } from '../../context/global_context'
import { deleteRequest, getRequest, postRequest } from '../../services/base'

const ConnectorViewForm = (props) => {
  const { vehicle, connector_view } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const [value, dispatch] = useContext(NavContext)

  const [connectorView, setConnectorView] = useState({})
  const references = useRef([])
  const [selectedLevelsIds, setSelectedLevelsIds] = useState([])
  const [harnesses, setHarnesses] = useState([])
  const [state, setState] = useState({
    selectedHarnesses: [],
    openedHarnesses: [],
    connectorViewName: '',
    checkedHarnessLevels: [],
    loading: false,
    svg: '',
    png: '',
    searchHarnessTableComponents: false,
    svg_removed: false,
    png_removed: false,
  })

  const { persisted, clone_from } = props

  useEffect(() => {
    dispatch({
      type: 'header',
      data: {
        header: vehicle,
        sub_header: connector_view ? 'Edit Vehicle View' : 'New Vehicle View',
        id: '',
        show_back_btn: true,
        dtc_details: {},
        vehicle: vehicle,
        back_btn_url: `/vehicles/${vehicle}/connector_views/`,
      },
    })

    if (connector_view) {
      getConnectorViewData(connector_view)
    }
    if (searchParams.get('clone_from') && clone_from) {
      getConnectorViewData(searchParams.get('clone_from'))
    }
  }, [])

  const getConnectorViewData = async (id) => {
    setState({ ...state, loading: true })

    let res = await getRequest(`/api/connector_views/${id}/`)
    setConnectorView(res)
    setHarnesses(res?.harness_ids)
    setSelectedLevelsIds(res?.harness_levels_ids)
    setState({
      ...state,
      loading: false,
      selectedHarnesses: res?.harness_ids,
      checkedHarnessLevels: res?.harness_levels_ids,
      connectorViewName: res?.name,
      svg: res?.svg_name,
      png: res?.png_name,
    })
    references.current = res?.group_ids
  }

  const hlCheckHandler = (harnessLevelIds, action) => {
    const selectedHarnessLevels = new Set(state.checkedHarnessLevels || [])

    harnessLevelIds.forEach((hl) => {
      if (
        (selectedHarnessLevels.has(hl) && action !== SELECT_ALL) ||
        action === DESELECT_ALL
      ) {
        selectedHarnessLevels.delete(hl)
      } else {
        selectedHarnessLevels.add(hl)
      }
    })

    setState({
      ...state,
      checkedHarnessLevels: Array.from(selectedHarnessLevels),
    })
  }

  const connectorViewNameChange = (event) => {
    setState({ ...state, connectorViewName: event.target.value })
  }

  const selectHarness = (harness) => {
    setState({
      ...state,
      selectedHarnesses: [...state.selectedHarnesses, harness],
      openedHarnesses: [...state.openedHarnesses, harness.id],
      searchHarnessTableComponents: false,
    })
  }

  const clearHarness = (harness, harnessLevels) => {
    const harnesses = state.selectedHarnesses
    const index = harnesses.indexOf(harness)
    if (index !== -1) {
      harnesses.splice(index, 1)
    }
    const harnessLevelsIds = harnessLevels.map((hl) => hl.id)
    const afterClear = state.checkedHarnessLevels.filter(
      (sl) => !harnessLevelsIds.includes(sl)
    )
    setState({
      ...state,
      selectedHarnesses: harnesses,
      checkedHarnessLevels: afterClear,
    })
  }

  const renderHarnessSearchTable = () => {
    setState({
      ...state,
      searchHarnessTableComponents: true,
      openedHarnesses: [],
    })
  }

  const hideHarnessSearchTable = () => {
    setState({ ...state, searchHarnessTableComponents: false })
  }

  const toggleHarnesses = (harness) => {
    let openedHarnesses = [...state.openedHarnesses]
    if (openedHarnesses.includes(harness.id)) {
      setState({
        ...state,
        openedHarnesses: openedHarnesses.filter((h) => h !== harness.id),
      })
    } else {
      setState({ ...state, openedHarnesses: [...openedHarnesses, harness.id] })
    }
  }

  const harnessIsOpened = (h) => {
    return state.openedHarnesses.includes(h.id)
  }

  const fileChanged = (e, type) => {
    const input = e.target

    if (input.value) {
      if (type === 'svg') {
        changeFile(input.files[0])
      }
      setState({
        ...state,
        [`${type}`]: input.files[0].name,
        [`${type}_removed`]: false,
      })
    }
  }

  const clearFile = (type) => {
    if (window.confirm(`Are you really wan't to remove ${type}`) == true) {
      setState({ ...state, [`${type}`]: null, [`${type}_removed`]: true })
      references.current = []
      if (connectorView?.id) {
        deleteImages(type)
      }
    }
  }

  const deleteImages = async (type) => {
    let res = await deleteRequest(
      `/api/connector_views/delete_views/${connectorView?.id}/${type}/`
    )

    dispatch({
      type: 'notification',
      data: {
        notification_type: 'success',
        msg: `${type} Successfully Delete`,
      },
    })

    getConnectorViewData(connectorView?.id)
  }

  const readFile = (event) => {
    const parser = new DOMParser()
    const svgDoc = parser.parseFromString(event.target.result, 'image/svg+xml')

    let referenceIds = []
    svgDoc.childNodes.forEach((main, index) => {
      main.childNodes.forEach((parent, parentIndex) => {
        parent.childNodes.forEach((child, childIndex) => {
          if (child.id) {
            referenceIds.push(child.id)
          }
        })
      })
    })
    references.current = referenceIds
  }

  const changeFile = () => {
    let element = document.getElementById('svg_attachment')
    var file = element.files[0]
    var reader = new FileReader()
    reader.addEventListener('load', readFile)
    reader.readAsText(file)
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    setState({ ...state, loading: true })

    const form = e.target
    const formData = new FormData(form)

    formData.append('png_removed', state.png_removed)
    formData.append('svg_removed', state.svg_removed)

    references.current.forEach((reference) => {
      formData.append('references', reference)
    })

    if (searchParams.get('clone_from') && clone_from) {
      formData.append('clone_from_id', searchParams.get('clone_from'))
      formData.append('png_name', connectorView?.png_name)
      formData.append('svg_name', connectorView?.svg_name)
    }

    let res = await postRequest(
      `/api/connector_views/save_connector_view/`,
      formData
    )
    setState({ ...state, loading: false })

    if (res?.non_field_errors) {
      dispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: `Error :- ${res?.non_field_errors.toString()}`,
        },
      })
    } else {
      if (connectorView?.id && !clone_from) {
        document.getElementById('svg_attachment').value = null
        document.getElementById('png_attachment').value = null
        getConnectorViewData(connectorView?.id)
      } else {
        navigate(
          `/vehicles/${vehicle}/connector_views/${res?.id}/edit_mappings`
        )
      }
    }
  }

  return (
    <div className="flex items-stretch flex-grow">
      <LoadingSpinnerOverlay active={state.loading} />
      <div className="flex-1 w-1/2 flex flex-col bg-tracer-darker-blue pt-6 flex-grow">
        {state?.checkedHarnessLevels?.length > 0 ||
        state?.selectedHarnesses?.length > 0 ? (
          state.selectedHarnesses.map((selectedHarness) => (
            <HarnessLevelsSearchTable
              key={`${selectedHarness.id}-harness-level`}
              harness={selectedHarness}
              checkedHarnessLevels={state.checkedHarnessLevels}
              onSelectHarnessLevel={(id, action) => hlCheckHandler(id, action)}
              onClearHarness={clearHarness}
              toggleHarnesses={toggleHarnesses}
              harnessIsOpened={harnessIsOpened}
              vehicle={vehicle}
            />
          ))
        ) : (
          <HarnessesSearchTable
            selectedHarnessesIds={state.selectedHarnesses.map((sh) => sh.id)}
            onSelectHarness={selectHarness}
            vehicle={vehicle}
          />
        )}

        {state.searchHarnessTableComponents && (
          <div className="flex flex-col">
            <HarnessesSearchTable
              selectedHarnessesIds={state.selectedHarnesses.map((sh) => sh.id)}
              onSelectHarness={selectHarness}
              vehicle={vehicle}
            />
            <button onClick={hideHarnessSearchTable} className="btn my-8 mx-4">
              Hide Harness Search Table
            </button>
          </div>
        )}
        {state.searchHarnessTableComponents === false &&
          state.selectedHarnesses.length > 0 && (
            <button
              onClick={renderHarnessSearchTable}
              className="btn my-8 mx-4"
            >
              Add Harness
            </button>
          )}
      </div>

      <form
        className="flex-1 w-1/2 flex flex-col p-4 pt-6 bg-tracer-dark-blue text-white flex-grow"
        action={''}
        acceptCharset="UTF-8"
        method="post"
        onSubmit={onSubmit}
        encType="multipart/form-data"
      >
        <input type="hidden" name="clone_from" value={clone_from} />
        <input type="hidden" name="vehicle_key" value={vehicle} />
        {persisted && connectorView?.id && (
          <input name="id" type="hidden" value={connectorView?.id} />
        )}
        {state.selectedHarnesses.map((selectedHarness, i) => (
          <Fragment key={`select-hrn-${selectedHarness.id}`}>
            {i === 0 && (
              <input type="hidden" name="harness" value={selectedHarness.id} />
            )}
            <input
              type="hidden"
              name="harness_ids"
              value={selectedHarness.id}
            />
          </Fragment>
        ))}
        {state.checkedHarnessLevels.map((hl, i) => (
          <input
            key={`checked-harness-${i}`}
            type="hidden"
            name="harness_levels_ids"
            value={hl}
          />
        ))}
        <div className="flex">Vehicle View Name</div>
        <div className="flex mt-6">
          <input
            autoComplete="off"
            name={'name'}
            className="w-full bg-white  text-black h-10 px-5 pr-5 rounded focus:outline-none"
            placeholder="Front View"
            onChange={connectorViewNameChange}
            value={state.connectorViewName || ''}
            onKeyDown={handleEnterKey}
            required={true}
          />
        </div>

        {persisted && (
          <div>
            <div className="flex mt-6">Current Image</div>

            <img src={connectorView.png} />
          </div>
        )}

        <div className="flex mt-6">Images</div>

        {persisted && (
          <div>
            <a
              className={'inline-block text-blue-500 underline cursor-pointer'}
              onClick={() => {
                downloadAsZip(connectorView?.id, connectorView?.name, dispatch)
              }}
            >
              Download persisted as ZIP
            </a>
          </div>
        )}

        <div className="flex flex-col w-full mt-6">
          <label htmlFor="svg_attachment">SVG</label>
          <input
            name="svg"
            type="file"
            id="svg_attachment"
            onChange={(e) => fileChanged(e, 'svg')}
            style={{ display: state.svg ? 'none' : 'inline' }}
            accept={'image/svg'}
          />
          {state.svg && (
            <div>
              {!clone_from && (
                <XCircle
                  className={'w-4 text-red-500 mr-3'}
                  onClick={(e) => clearFile('svg')}
                />
              )}
              {state.svg}
            </div>
          )}
          {persisted && (
            <div className="text-red-600 text-sm">
              Updating the SVG image will erase all existing mappings!
            </div>
          )}
          <label htmlFor="png_attachment">PNG</label>
          <input
            name="png"
            type="file"
            id="png_attachment"
            onChange={(e) => fileChanged(e, 'png')}
            style={{ display: state.png ? 'none' : 'inline' }}
            accept={'image/svg'}
          />
          {state.png && (
            <div>
              {!clone_from && (
                <XCircle
                  className={'w-4 text-red-500 mr-3'}
                  onClick={(e) => clearFile('png')}
                />
              )}
              {state.png}
            </div>
          )}
        </div>
        <div className="flex mt-20">
          <button type="submit" className="btn my-4 w-full">
            {persisted ? 'Save Vehicle View Changes' : 'Add Vehicle View'}
          </button>
        </div>
      </form>
    </div>
  )
}

ConnectorViewForm.propTypes = {
  harnesses: PropTypes.array,
  selectedLevelsIds: PropTypes.array,
  persisted: PropTypes.bool,
  connectorView: PropTypes.object,
  clone_from: PropTypes.bool,
}

export default ConnectorViewForm
